import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Users, Clock, AlertTriangle, Building2, Target, Phone } from 'lucide-react';

// Section "Personnel actif" (cartes avec alerte fatigue >12h)
export const EngagedPersonnelSection = ({ engaged }) => (
    <Card className="content-card" data-testid="engaged-secouristes">
        <CardHeader className="card-header">
            <CardTitle className="card-title flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-500" />
                Personnel actif ({engaged.length})
            </CardTitle>
        </CardHeader>
        <CardContent>
            {engaged.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {engaged.map(s => (
                        <div key={s.user_id} className={`p-3 rounded-lg border ${s.alert_12h ? 'bg-red-500/10 border-red-500/40' : 'bg-zinc-800/50 border-zinc-700/50'}`} data-testid={`engaged-${s.user_name}`}>
                            <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-white text-sm">{s.user_name}</span>
                                {s.alert_12h && (
                                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px]">+12h</Badge>
                                )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-zinc-400">
                                <Clock className="w-3 h-3" />
                                <span>Actif depuis {s.duration_hours}h</span>
                            </div>
                            {s.alert_12h && (
                                <div className="flex items-center gap-1 text-xs mt-1 text-red-400 font-semibold">
                                    <AlertTriangle className="w-3 h-3" />
                                    <span>ALERTE FATIGUE</span>
                                </div>
                            )}
                            {s.center_name && (
                                <p className="text-xs text-cyan-400 mt-1 flex items-center gap-1"><Building2 className="w-3 h-3" />{s.center_name}</p>
                            )}
                            {s.mission_name && (
                                <p className="text-xs text-amber-400 mt-1 flex items-center gap-1"><Target className="w-3 h-3" />{s.mission_name}</p>
                            )}
                            {s.comment && (
                                <p className="text-[10px] text-zinc-500 mt-1">{s.comment}</p>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="p-6 text-center text-zinc-500">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucun personnel actif</p>
                </div>
            )}
        </CardContent>
    </Card>
);

// Dialog "Personnel actif" (ouvert depuis la stat card)
export const PersonnelDialog = ({ open, onOpenChange, engaged }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-teal-500" />
                    Personnel actif
                </DialogTitle>
                <DialogDescription className="sr-only">Liste du personnel actuellement en service avec leur duree de travail</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
                {engaged.length > 0 ? engaged.map(s => (
                    <div key={s.user_id} className={`p-4 rounded-lg border ${s.alert_12h ? 'bg-red-500/10 border-red-500/40' : 'bg-zinc-800/50 border-zinc-700/50'}`} data-testid={`personnel-dialog-${s.user_name}`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium text-white">{s.user_name}</p>
                                {s.center_name && <p className="text-xs text-cyan-400 flex items-center gap-1 mt-0.5"><Building2 className="w-3 h-3" />{s.center_name}</p>}
                                {s.mission_name && <p className="text-xs text-amber-400 flex items-center gap-1 mt-0.5"><Target className="w-3 h-3" />{s.mission_name}</p>}
                                {s.comment && <p className="text-[10px] text-zinc-500 mt-1">{s.comment}</p>}
                            </div>
                            <div className="text-right">
                                <p className={`text-sm font-mono font-bold ${s.alert_12h ? 'text-red-400' : 'text-white'}`}>{s.duration_hours}h</p>
                                {s.alert_12h && <p className="text-[10px] text-red-400 font-semibold">ALERTE +12h</p>}
                            </div>
                        </div>
                    </div>
                )) : (
                    <div className="p-8 text-center text-zinc-500">
                        <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Aucun personnel actif</p>
                    </div>
                )}
            </div>
        </DialogContent>
    </Dialog>
);

// Acces rapide annuaire de crise
export const CrisisDirectoryCard = ({ directory }) => (
    <Card className="content-card" data-testid="crisis-directory-quick">
        <CardHeader className="card-header">
            <CardTitle className="card-title flex items-center gap-2">
                <Phone className="w-5 h-5 text-green-500" />
                Annuaire de crise (contacts rapides)
            </CardTitle>
        </CardHeader>
        <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {directory.slice(0, 6).map((contact) => (
                    <a
                        key={contact.id}
                        href={`tel:${contact.phone}`}
                        className="p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors"
                    >
                        <p className="font-medium text-white text-sm truncate">{contact.name}</p>
                        <p className="text-xs text-zinc-500">{contact.role}</p>
                        <p className="text-sm text-blue-400 mt-1 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {contact.phone}
                        </p>
                    </a>
                ))}
            </div>
        </CardContent>
    </Card>
);
